import { Link } from "react-router-dom";

// Full-length, SEO-optimised articles. Internal links point to public pages
// (/taper, /find-support, /articles/...) and the sign-up flow (/auth).

export const ARTICLES = [
  {
    slug: "uk-benzodiazepine-epidemic",
    title: "The UK & Ireland Benzodiazepine Problem: How We Got Here",
    description:
      "A plain-English look at the benzodiazepine epidemic in the UK and Ireland — how prescribing grew, why street benzos took hold, and how to reduce safely.",
    category: "Awareness",
    readTime: "6 min read",
    image:
      "https://images.unsplash.com/photo-1600010101590-fbc57640c470?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
    excerpt:
      "How did benzodiazepines become such a widespread problem across Britain and Ireland — and what can you do about it today?",
    Content: () => (
      <>
        <p>
          Benzodiazepines — often shortened to "benzos" — are among the most widely used
          prescription medicines in the world. In the UK and Ireland they are prescribed for
          anxiety, insomnia, seizures and muscle spasm, usually under names like diazepam
          (Valium), lorazepam, temazepam, nitrazepam and alprazolam (Xanax). They work, and for
          short-term use they can genuinely help. The problem is what happens over the longer term.
        </p>

        <h2>What benzodiazepines actually do</h2>
        <p>
          Benzodiazepines calm the nervous system by boosting the effect of a brain chemical called
          GABA. That is why they ease anxiety and help sleep. But the brain adapts quickly. Within a
          few weeks of regular use, the same dose starts to do less (tolerance), and the body begins
          to rely on the drug to feel normal (dependence). This is a physical process — not a lack of
          willpower — and it can happen even when the medication is taken exactly as prescribed.
        </p>

        <h2>How prescribing grew</h2>
        <p>
          Benzodiazepines were introduced in the 1960s as a "safer" alternative to barbiturates and
          were prescribed enthusiastically for decades. Official guidance in both the UK and Ireland
          now recommends short courses only — typically no more than two to four weeks — yet many
          people have remained on them for years, sometimes decades. Repeat prescriptions, busy
          clinics and the very real difficulty of stopping all played a part.
        </p>

        <h2>The shift to illicit and "street" benzos</h2>
        <p>
          More recently, a second wave has emerged: illicitly produced benzodiazepines sold online
          and on the street. These are pressed into tablets that look like genuine medication but
          often contain unknown, novel benzodiazepines at unpredictable strengths. This has driven a
          sharp rise in drug-related deaths, particularly in Scotland, and is a growing concern
          across England, Wales, Northern Ireland and Ireland. We cover this in detail in{" "}
          <Link to="/articles/fake-counterfeit-benzos-dangers">
            our guide to fake and counterfeit benzos
          </Link>
          .
        </p>

        <h2>The human cost — and the hope</h2>
        <p>
          Long-term dependence affects sleep, memory, mood and relationships, and stopping suddenly
          can be dangerous. But here is the important part: with a slow, supported reduction, the
          nervous system does recover. People come off benzodiazepines every day and rebuild their
          lives. Recovery is rarely a straight line, and a harder day is not a failure — it is part
          of the journey.
        </p>

        <h2>What you can do</h2>
        <ul>
          <li>
            Never stop abruptly. Use our{" "}
            <Link to="/articles/how-to-taper-off-benzodiazepines-safely">
              guide to tapering safely
            </Link>{" "}
            and try the{" "}
            <Link to="/taper">TaperPath Taper Tool</Link> to see a gentle reference schedule you can
            discuss with your prescriber.
          </li>
          <li>
            Talk to your GP. The Taper Tool can generate a letter to help start that conversation.
          </li>
          <li>
            Find support near you with our{" "}
            <Link to="/find-support">Find Support locator</Link>, which now includes live,
            CQC-verified services across England alongside curated services in Scotland, Wales,
            Northern Ireland and Ireland.
          </li>
        </ul>

        <p>
          <em>
            This article is educational harm-reduction information, not medical advice. Any change to
            your benzodiazepine dose should be agreed and supervised by a doctor or prescriber.
          </em>
        </p>
      </>
    ),
  },

  {
    slug: "fake-counterfeit-benzos-dangers",
    title: "Fake & Counterfeit \"Street\" Benzos: Why They're So Dangerous",
    description:
      "Counterfeit benzodiazepines sold as Xanax or Valium can contain unknown, ultra-strong drugs. Learn the risks and practical harm-reduction steps.",
    category: "Harm reduction",
    readTime: "6 min read",
    image:
      "https://images.unsplash.com/photo-1652038448592-27377ec0b7d4?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
    excerpt:
      "A blue \"Xanax\" bar bought online is rarely what it claims to be. Here's why counterfeit benzos are so risky — and how to stay safer.",
    Content: () => (
      <>
        <p>
          If a benzodiazepine tablet did not come from a pharmacy against a prescription, there is no
          reliable way to know what is actually in it. Counterfeit "street" benzos — often sold as
          Xanax bars, blue Valium (diazepam) or "blues" — are one of the biggest hidden dangers in
          drug-related harm across the UK and Ireland today.
        </p>

        <h2>What are counterfeit benzos?</h2>
        <p>
          These are tablets pressed in unregulated labs to imitate genuine medication. They frequently
          contain <strong>novel benzodiazepines</strong> such as etizolam, flualprazolam,
          bromazolam or flubromazolam — substances that are not licensed medicines and can be many
          times stronger than the drug they are pretending to be. Two tablets from the same bag can
          contain wildly different amounts.
        </p>

        <h2>Why they are so dangerous</h2>
        <ul>
          <li>
            <strong>Unknown strength:</strong> A single pressed tablet may contain a tiny dose or a
            dangerously large one. There is no quality control.
          </li>
          <li>
            <strong>Extreme potency:</strong> Some novel benzos are active at fractions of a
            milligram, making accidental overdose easy.
          </li>
          <li>
            <strong>Contamination:</strong> Counterfeit tablets have increasingly been found to
            contain synthetic opioids, including nitazenes, which can stop breathing.
          </li>
          <li>
            <strong>Dangerous combinations:</strong> Mixing benzos with alcohol, opioids
            (including heroin or methadone), pregabalin or gabapentin sharply raises the risk of
            fatal respiratory depression.
          </li>
        </ul>

        <h2>Practical harm-reduction steps</h2>
        <p>
          The safest option is always a prescribed, pharmacy-supplied medicine reduced under medical
          supervision — see our{" "}
          <Link to="/articles/how-to-taper-off-benzodiazepines-safely">tapering guide</Link>. If you
          are using street benzos right now, these steps can reduce the risk:
        </p>
        <ul>
          <li>Never use alone. Have someone with you who can call 999 if needed.</li>
          <li>Avoid mixing with alcohol, opioids or other sedatives.</li>
          <li>
            Start with a very small amount — you cannot judge strength by appearance, colour or
            markings.
          </li>
          <li>
            Carry naloxone if there is any chance of opioid contamination, and know it does not
            reverse benzodiazepines but can reverse opioids.
          </li>
          <li>Know the signs of overdose: unresponsiveness, slow or stopped breathing, blue lips.</li>
        </ul>

        <h2>Getting support</h2>
        <p>
          Local drug and alcohol services can offer non-judgemental advice, safer-use guidance and a
          route to prescribed treatment. Use the{" "}
          <Link to="/find-support">Find Support locator</Link> to find services near you, or read{" "}
          <Link to="/articles/finding-recovery-support-uk-ireland">
            how to find recovery support across the UK &amp; Ireland
          </Link>
          . You deserve help, whatever you are using.
        </p>

        <p>
          <em>
            If you or someone else may have overdosed, call 999 immediately. This article is
            harm-reduction information, not medical advice.
          </em>
        </p>
      </>
    ),
  },

  {
    slug: "how-to-taper-off-benzodiazepines-safely",
    title: "How to Taper Off Benzodiazepines Safely: The Gradual Approach",
    description:
      "A step-by-step, plain-English guide to reducing benzodiazepines gradually and safely, based on Ashton Manual principles — with tools to help.",
    category: "Tapering",
    readTime: "7 min read",
    image:
      "https://images.unsplash.com/photo-1551342909-187e24a5797d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
    excerpt:
      "Coming off benzodiazepines is very achievable when it's done slowly and with support. Here's what a safe taper looks like.",
    Content: () => (
      <>
        <p>
          Reducing benzodiazepines is one of the most common questions people ask us — and the good
          news is that it is very achievable when it is done gradually and with support. The single
          most important rule is simple: <strong>never stop suddenly.</strong>
        </p>

        <h2>Why you should never stop abruptly</h2>
        <p>
          Because the brain has adapted to the drug, stopping all at once can cause severe withdrawal,
          including anxiety, insomnia, seizures and, rarely, life-threatening complications. A slow
          taper gives your nervous system time to readjust, keeping symptoms manageable.
        </p>

        <h2>The gradual (~10%) approach</h2>
        <p>
          The widely used principle, drawn from{" "}
          <a href="https://benzo.org.uk/manual" target="_blank" rel="noreferrer">
            The Ashton Manual
          </a>
          , is to reduce by roughly 10% of your current dose every one to two weeks, holding steady
          whenever symptoms flare. Because reductions are a percentage of the current dose, the
          amount you cut gets smaller as you go — which is exactly why the final stages are often
          taken very slowly.
        </p>

        <h2>Switching to a longer-acting benzodiazepine</h2>
        <p>
          Short-acting benzodiazepines (like lorazepam or alprazolam) leave the body quickly, which
          can cause symptoms between doses. Prescribers often switch people to an equivalent dose of
          a longer-acting drug such as diazepam to smooth the process. Our{" "}
          <Link to="/taper">Taper Tool</Link> shows diazepam-equivalent doses and a reference
          schedule based on your current medication and dose.
        </p>

        <h2>Holds and flexibility</h2>
        <p>
          A taper is not a race. If a reduction feels too hard, it is completely fine to hold at the
          same dose for longer before the next cut. Many people find that a slower, steadier pace is
          far more comfortable and sustainable than pushing through. There is no single "correct"
          speed — the right pace is the one you and your prescriber can maintain.
        </p>

        <h2>Working with your GP</h2>
        <p>
          Tapering should always be supervised by a doctor or prescriber. Bring a clear plan to the
          conversation: the TaperPath{" "}
          <Link to="/taper">Taper Tool</Link> can generate a printable GP letter summarising your
          medication, dose and a reference schedule to discuss together (you'll be asked to create a
          quick free account to save and print it).
        </p>

        <h2>Track how you feel</h2>
        <p>
          Keeping a simple daily record of dose, sleep and mood helps you and your prescriber see
          patterns and choose the right moment for each reduction. A{" "}
          <Link to="/auth">free TaperPath account</Link> lets you log your day, see gentle progress
          (never a shame counter) and export a weekly summary for your GP.
        </p>

        <p>
          For what to expect along the way, read{" "}
          <Link to="/articles/benzo-withdrawal-symptoms-and-what-helps">
            benzo withdrawal symptoms and what actually helps
          </Link>
          , and when you're ready, find local and online help with our{" "}
          <Link to="/find-support">Find Support locator</Link>.
        </p>

        <p>
          <em>
            This is educational information, not a prescription. Always taper under the guidance of a
            doctor or prescriber, and never stop benzodiazepines abruptly.
          </em>
        </p>
      </>
    ),
  },

  {
    slug: "benzo-withdrawal-symptoms-and-what-helps",
    title: "Benzo Withdrawal Symptoms & What Actually Helps",
    description:
      "Common benzodiazepine withdrawal symptoms explained, plus practical, compassionate strategies for sleep, anxiety and the 'windows and waves' pattern.",
    category: "Wellbeing",
    readTime: "6 min read",
    image:
      "https://images.unsplash.com/photo-1512052841874-42cbf0701904?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
    excerpt:
      "Withdrawal can be uncomfortable, but it is temporary and it does ease. Here's what to expect and what genuinely helps.",
    Content: () => (
      <>
        <p>
          Withdrawal is the nervous system relearning how to run without the drug. It can be
          uncomfortable, but it is temporary — and understanding what is happening makes it far less
          frightening.
        </p>

        <h2>Common symptoms</h2>
        <ul>
          <li>Anxiety, restlessness and a sense of being "wired"</li>
          <li>Broken sleep, vivid dreams or insomnia</li>
          <li>Muscle tension, aches and tremor</li>
          <li>Heightened senses — light, sound and smell feeling too intense</li>
          <li>Low mood, tearfulness and emotional sensitivity</li>
          <li>Brain fog, poor concentration and memory blips</li>
        </ul>
        <p>
          These vary hugely from person to person and are usually gentler when you follow a slow,
          steady taper — see{" "}
          <Link to="/articles/how-to-taper-off-benzodiazepines-safely">
            how to taper off benzodiazepines safely
          </Link>
          .
        </p>

        <h2>Windows and waves</h2>
        <p>
          Recovery rarely improves in a straight line. Most people experience "windows and waves" —
          good stretches where you feel almost normal, followed by harder patches. A wave does not
          mean you are going backwards; it is a normal part of healing, and the windows tend to grow
          longer over time.
        </p>

        <h2>Protracted withdrawal</h2>
        <p>
          For a minority of people, some symptoms linger for months after the last dose. This is
          sometimes called protracted withdrawal. It is real, it usually continues to improve, and it
          does not mean you have done anything wrong. Staying connected to support during this phase
          matters enormously.
        </p>

        <h2>What actually helps</h2>
        <ul>
          <li>
            <strong>Sleep:</strong> keep a consistent bedtime, dim lights in the evening, avoid
            caffeine after midday and build a calming wind-down routine.
          </li>
          <li>
            <strong>Anxiety:</strong> slow breathing, gentle movement, and getting outside in
            daylight all help settle the nervous system.
          </li>
          <li>
            <strong>Diet:</strong> regular, balanced meals and staying hydrated steady energy and
            mood.
          </li>
          <li>
            <strong>Pacing:</strong> lower your expectations of yourself on wave days — rest is
            productive.
          </li>
          <li>
            <strong>Connection:</strong> a{" "}
            <Link to="/auth">free account</Link> lets you set gentle reminders, journal how you feel,
            and share milestones with a trusted buddy or the peer community.
          </li>
        </ul>

        <h2>When to seek urgent help</h2>
        <p>
          Contact your prescriber promptly if symptoms become severe, and seek emergency help (999)
          for any seizure, chest pain or thoughts of harming yourself. If you are struggling
          emotionally, the Samaritans are available free, day or night, on 116 123.
        </p>

        <p>
          When you are ready to build your support network, our{" "}
          <Link to="/find-support">Find Support locator</Link> can help you find local and online
          services.
        </p>

        <p>
          <em>This is educational information, not medical advice.</em>
        </p>
      </>
    ),
  },

  {
    slug: "finding-recovery-support-uk-ireland",
    title: "How to Find Recovery Support Across the UK & Ireland",
    description:
      "NHS services, national helplines, charities and peer support for benzodiazepine and drug recovery in England, Scotland, Wales, Northern Ireland and Ireland.",
    category: "Support",
    readTime: "6 min read",
    image:
      "https://images.unsplash.com/photo-1721059050924-eaf014837bd1?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
    excerpt:
      "You do not have to do this alone. Here's a practical map of the free support available across the UK and Ireland.",
    Content: () => (
      <>
        <p>
          Whatever your situation, free and confidential support is available across the UK and
          Ireland — and reaching out is a sign of strength, not weakness. Here is a practical map of
          where to turn.
        </p>

        <h2>NHS and community drug &amp; alcohol services</h2>
        <p>
          Every area has a local, NHS-commissioned drug and alcohol service offering free advice,
          key-working, prescribing and structured support. You can usually self-refer — you do not
          always need to go through your GP. In England, many of these services are registered and
          inspected by the Care Quality Commission (CQC), and TaperPath now surfaces them live in the{" "}
          <Link to="/find-support">Find Support locator</Link>.
        </p>

        <h2>National helplines</h2>
        <ul>
          <li>
            <strong>England:</strong> FRANK — 0300 123 6600, or{" "}
            <a href="https://www.talktofrank.com" target="_blank" rel="noreferrer">
              talktofrank.com
            </a>
          </li>
          <li>
            <strong>Scotland:</strong> Know the Score — 0800 587 5879
          </li>
          <li>
            <strong>Wales:</strong> DAN 24/7 — 0808 808 2234
          </li>
          <li>
            <strong>Northern Ireland:</strong> via your GP or the Public Health Agency drug &amp;
            alcohol services
          </li>
          <li>
            <strong>Ireland:</strong> HSE Drugs &amp; Alcohol Helpline — 1800 459 459
          </li>
        </ul>

        <h2>Charities and treatment providers</h2>
        <p>
          National charities such as We Are With You, Turning Point and Change Grow Live (CGL)
          deliver many local services and offer online support and webchat. Residential
          rehabilitation is also an option for some people, offering medically supervised detox and
          longer programmes.
        </p>

        <h2>Peer support and community</h2>
        <p>
          Peer support can be transformational. SMART Recovery runs free online and in-person
          meetings using practical tools, and peer-led communities like Path 2 Recovery offer
          mentoring and belonging. Inside TaperPath, a{" "}
          <Link to="/auth">free account</Link> gives you a moderated peer community, a buddy system to
          share milestones with someone you trust, and gentle reminders and journaling.
        </p>

        <h2>Using the Find Support locator</h2>
        <p>
          Our <Link to="/find-support">Find Support locator</Link> lets you search by postcode or
          town and filter by service type. Listings tagged <strong>CQC</strong> are pulled live from
          the Care Quality Commission for England, while services in Scotland, Wales, Northern
          Ireland and Ireland are curated by our team. If you know a service we are missing, you can
          suggest it right from the page.
        </p>

        <p>
          Not sure where to start? Begin with the{" "}
          <Link to="/articles/how-to-taper-off-benzodiazepines-safely">tapering guide</Link> and the{" "}
          <Link to="/taper">Taper Tool</Link>, then reach out to one service today. One small step is
          enough.
        </p>

        <p>
          <em>
            If you are in immediate danger or crisis, call 999, or the Samaritans free on 116 123.
          </em>
        </p>
      </>
    ),
  },
];

export const getArticle = (slug) => ARTICLES.find((a) => a.slug === slug);
